import { useEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries } from "lightweight-charts";

export const normalizeChartTime = (value) => {
  if (value == null) return null;

  if (typeof value === "number") {
    return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
  }

  const numeric = Number(value);
  if (!Number.isNaN(numeric) && String(value).trim() !== "") {
    return numeric > 1e12 ? Math.floor(numeric / 1000) : Math.floor(numeric);
  }

  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
};

export const FibonacciChart = (props) => {
  const {
    candles,
    fibonacci,
    tradeMarkers,
    openTrades = [],
    tradeHistory = [],
    loading,
    swingPoints,
    showRedrawAnnotation = true
  } = props;
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(600);
  const [chartReady, setChartReady] = useState(false);
  const [chartVersion, setChartVersion] = useState(0);
  const [historicalLevels, setHistoricalLevels] = useState([]);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: { background: { type: "solid", color: "#000000" }, textColor: "#A1A1AA", fontFamily: "JetBrains Mono, monospace" },
      localization: { locale: "en-US" },
      grid: { vertLines: { visible: false }, horzLines: { visible: false } },
      crosshair: { mode: 1, vertLine: { color: "#52525B", width: 1, style: 2, labelBackgroundColor: "#27272A" }, horzLine: { color: "#52525B", width: 1, style: 2, labelBackgroundColor: "#27272A" } },
      timeScale: { borderColor: "#27272A", timeVisible: true, secondsVisible: false, barSpacing: 8 },
      rightPriceScale: { borderColor: "#27272A", scaleMargins: { top: 0.1, bottom: 0.2 } },
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#22C55E", downColor: "#EF4444",
      borderUpColor: "#22C55E", borderDownColor: "#EF4444",
      wickUpColor: "#22C55E", wickDownColor: "#EF4444",
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;

    chart.subscribeCrosshairMove((param) => {
      const tooltip = document.getElementById('ohlc-tooltip');
      if (!tooltip) return;
      if (!param.time || !param.seriesData) { tooltip.style.display = 'none'; return; }
      const data = param.seriesData.get(candleSeries);
      if (!data) { tooltip.style.display = 'none'; return; }
      const date = new Date(param.time * 1000).toISOString().replace('T',' ').substring(0,16) + ' UTC';
      tooltip.innerHTML = '<span style="color:#71717A">' + date + '</span> &nbsp; O <span style="color:#A1A1AA">' + data.open.toFixed(2) + '</span> H <span style="color:#22C55E">' + data.high.toFixed(2) + '</span> L <span style="color:#EF4444">' + data.low.toFixed(2) + '</span> C <span style="color:#A1A1AA">' + data.close.toFixed(2) + '</span>';
      tooltip.style.display = 'block';
    });

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        const w = chartContainerRef.current.clientWidth;
        const h = chartContainerRef.current.clientHeight;
        chartRef.current.applyOptions({ width: w, height: h });
        setChartHeight(h);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
      if (chartRef.current) chartRef.current.remove();
    };
  }, []);

  useEffect(() => {
    fetch("/api/historical-levels")
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.levels) setHistoricalLevels(data.levels.filter((z) => z.display));
      })
      .catch(() => setHistoricalLevels([]));
  }, []);

  useEffect(() => {
    if (!candleSeriesRef.current || !candles || candles.length === 0) return;

    const toChartTime = (value) => {
      if (typeof value === "number") {
        return value > 1e12 ? Math.floor(value / 1000) : value;
      }
      const ms = new Date(value).getTime();
      return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
    };

    const formatted = candles
      .map((c) => {
        const t = toChartTime(c.time ?? c.timestamp);
        if (!t) return null;
        return {
          time: t,
          open: Number(c.open),
          high: Number(c.high),
          low: Number(c.low),
          close: Number(c.close),
        };
      })
      .filter((c) => c && !isNaN(c.open) && !isNaN(c.high) && !isNaN(c.low) && !isNaN(c.close));

    if (formatted.length === 0) return;

      candleSeriesRef.current.setData(formatted);
      if (!chartRef.current.__rangeSet) {
        const total = formatted.length;
        const visibleBars = 80;
        const to = Math.max(total - 1, 0);
        const from = Math.max(0, to - visibleBars);
        chartRef.current.timeScale().setVisibleLogicalRange({ from, to });
        chartRef.current.timeScale().applyOptions({ rightOffset: 6 });
        chartRef.current.__rangeSet = true;
      }
    setChartReady(true);
    chartRef.current.timeScale().subscribeVisibleLogicalRangeChange(() => setChartVersion(v => v + 1));
  }, [candles]);

  useEffect(() => {
    const series = candleSeriesRef.current;

    if (!series) return;

    if (!tradeMarkers || tradeMarkers.length === 0 || !candles || candles.length === 0) {
      series.setMarkers([]);
      return;
    }

    const nearestCandleTime = (markerTimeRaw) => {
      const markerTime = normalizeChartTime(markerTimeRaw);
      if (markerTime == null) return null;

      let bestTime = null;
      let bestDiff = Infinity;

      candles.forEach((candle) => {
        const candleTime = normalizeChartTime(candle.time ?? candle.timestamp ?? candle.timeMs);
        if (candleTime == null) return;

        const diff = Math.abs(candleTime - markerTime);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestTime = candleTime;
        }
      });

      return bestTime;
    };

    const nativeMarkers = tradeMarkers
      .map((marker) => {
        const direction = String(marker.direction || "").toUpperCase();
        const time = nearestCandleTime(marker.time);

        if (!time) return null;

        const priceText = marker.price != null
          ? Number(marker.price).toFixed(0)
          : "";

        const text = marker.label ||
          `${marker.engine || "—"} ${direction} @ ${priceText}`;

        return {
          time,
          position: direction === "SHORT" ? "aboveBar" : "belowBar",
          color: "#D946EF",
          shape: direction === "SHORT" ? "arrowDown" : "arrowUp",
          text,
        };
      })
      .filter(Boolean);

    series.setMarkers(nativeMarkers);
  }, [tradeMarkers, candles, chartReady]);

  const getPriceRange = () => {
    if (!candles || candles.length === 0) return { min: 0, max: 100 };
    let min = Infinity, max = -Infinity;
    for (let i = 0; i < candles.length; i++) {
      if (candles[i].low < min) min = candles[i].low;
      if (candles[i].high > max) max = candles[i].high;
    }
    return { min, max };
  };

  const priceToY = (price, priceRange, height) => {
    const range = priceRange.max - priceRange.min;
    if (range === 0) return 0;
    const paddedRange = range * 1.3;
    const paddedMin = priceRange.min - range * 0.2;
    const percentage = (price - paddedMin) / paddedRange;
    return (height - 30) * (1 - percentage);
  };

  const getSwingIndices = () => {
    if (!fibonacci || !candles || candles.length === 0) {
      return { fromIdx: -1, toIdx: -1 };
    }

    const toMs = (v) => {
      if (v == null) return null;
      if (typeof v === "number") return v > 1e12 ? v : v * 1000;
      const ms = Date.parse(v);
      return Number.isFinite(ms) ? ms : null;
    };

    const closestIndexByTime = (targetTime) => {
      const targetMs = toMs(targetTime);
      if (targetMs == null) return -1;

      let bestIdx = -1;
      let bestDiff = Infinity;

      candles.forEach((candle, idx) => {
        const candleMs = toMs(candle.timeMs ?? candle.timestamp ?? candle.time);
        if (candleMs == null) return;

        const diff = Math.abs(candleMs - targetMs);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestIdx = idx;
        }
      });

      return bestIdx;
    };

    const fromIdx = closestIndexByTime(fibonacci.swing_from_time);
    const toIdx = closestIndexByTime(fibonacci.swing_to_time);

    return { fromIdx, toIdx };
  };

  const renderHistoricalZones = () => {
    if (!historicalLevels || historicalLevels.length === 0 || !candles || candles.length === 0 || chartHeight < 100) return null;

    const series = candleSeriesRef.current;
    if (!series) return null;

    const containerWidth = chartContainerRef.current?.clientWidth ?? 800;
    const latest = candles[candles.length - 1];
    const currentPrice = Number(latest?.close);
    if (!Number.isFinite(currentPrice)) return null;

    const zones = historicalLevels
      .map((z) => ({
        ...z,
        zone_low: Number(z.zone_low),
        zone_high: Number(z.zone_high),
      }))
      .filter((z) => Number.isFinite(z.zone_low) && Number.isFinite(z.zone_high))
      .sort((a, b) => a.zone_low - b.zone_low);

    const currentZone = zones.find((z) => currentPrice >= z.zone_low && currentPrice <= z.zone_high);
    const supportBelow = [...zones].reverse().find((z) => z.zone_high < currentPrice);
    const resistanceAbove = zones.find((z) => z.zone_low > currentPrice);

    const visible = [];
    [currentZone, supportBelow, resistanceAbove].forEach((z) => {
      if (z && !visible.some((v) => v.id === z.id)) visible.push(z);
    });

    return visible.map((zone) => {
      const yHigh = series.priceToCoordinate(zone.zone_high);
      const yLow = series.priceToCoordinate(zone.zone_low);
      if (yHigh == null || yLow == null) return null;

      const top = Math.min(yHigh, yLow);
      const height = Math.abs(yLow - yHigh);
      if (!Number.isFinite(top) || !Number.isFinite(height) || height < 1) return null;
      if (top > chartHeight + 40 || top + height < -40) return null;

      const priceLabel = `$${zone.zone_low.toLocaleString("en-US")}–$${zone.zone_high.toLocaleString("en-US")}`;

      return (
        <div
          key={zone.id}
          style={{
            position: "absolute",
            left: 0,
            top: top + "px",
            width: containerWidth + "px",
            height: height + "px",
            background: "rgba(250, 204, 21, 0.035)",
            borderTop: "1px solid rgba(250, 204, 21, 0.25)",
            borderBottom: "1px solid rgba(250, 204, 21, 0.25)",
            pointerEvents: "none",
            zIndex: 5,
          }}
        >
          <div style={{
            position: "absolute",
            right: "90px",
            top: "4px",
            background: "rgba(0,0,0,0.72)",
            color: "#FACC15",
            padding: "2px 7px",
            borderRadius: "4px",
            fontSize: "10px",
            fontFamily: "JetBrains Mono, monospace",
            whiteSpace: "nowrap",
            border: "1px solid rgba(250,204,21,0.35)"
          }}>
            {zone.label} · {priceLabel}
          </div>
        </div>
      );
    });
  };

  const renderFibLevels = () => {
    if (!fibonacci || !candles || candles.length === 0 || chartHeight < 100) return null;
    const chartApi = chartRef.current;
    const series   = candleSeriesRef.current;
    if (!chartApi || !series) return null;
    const displayOpacity = fibonacci.displayOpacity ?? 1;
    if (displayOpacity === 0) return null;

    const containerWidth = chartContainerRef.current?.clientWidth ?? 800;

    const fibDefs = [
      { key: "fib_100",     pct: "100.0%", label: "SWING HIGH", color: "#EF4444", dash: "0",   width: 2 },
      { key: "fib_618",     pct: "61.8%",  label: "STOP ZONE",  color: "#EF4444", dash: "6,4", width: 1.5 },
      { key: "fib_50",      pct: "50.0%",  label: "ENTRY",      color: "#ffffff", dash: "0",   width: 2 },
      { key: "fib_382",     pct: "38.2%",  label: "",           color: "#52525B", dash: "4,4", width: 1 },
      { key: "fib_236",     pct: "23.6%",  label: "",           color: "#52525B", dash: "4,4", width: 1 },
      { key: "fib_0",       pct: "0.0%",   label: "SWING LOW",  color: "#38bdf8", dash: "0",   width: 1.5 },
      { key: "fib_target", pct: "-23.6%", label: "TARGET",     color: "#22C55E", dash: "6,4", width: 2 },
    ];

    return fibDefs.map(({ key, pct, label, color, dash, width }) => {
      const price = fibonacci[key];
      if (price == null) return null;
      const y = series.priceToCoordinate(price);
      if (y == null || !Number.isFinite(y)) return null;
      if (y < -20 || y > chartHeight + 20) return null;

      const borderStyle = dash === "0"
        ? `${width}px solid ${color}`
        : `${width}px dashed ${color}`;

      const isKey = key === "fib_100" || key === "fib_50" || key === "fib_0" || key === "fib_neg_236";
      const displayLabel = label ? `${pct} ${label}` : pct;

      return (
        <div
          key={key}
          style={{
            position:      "absolute",
            left:          0,
            top:           y + "px",
            width:         containerWidth + "px",
            opacity:       displayOpacity,
            pointerEvents: "none",
            zIndex:        20,
          }}
        >
          <div style={{ width: "100%", borderTop: borderStyle }} />
          <div style={{
            position:   "absolute",
            left:       "70px",
            top:        "-13px",
            background: "rgba(0,0,0,0.85)",
            color:      color,
            padding:    "1px 6px",
            borderRadius: "3px",
            fontSize:   "10px",
            fontFamily: "JetBrains Mono, monospace",
            whiteSpace: "nowrap",
            fontWeight: isKey ? "bold" : "normal",
          }}>
            {displayLabel}
          </div>
          <div style={{
            position:   "absolute",
            right:      "10px",
            top:        "-13px",
            background: "rgba(0,0,0,0.85)",
            color:      color,
            padding:    "1px 6px",
            borderRadius: "3px",
            fontSize:   "10px",
            fontFamily: "JetBrains Mono, monospace",
            whiteSpace: "nowrap",
          }}>
            ${price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      );
    });
    return levels.map((level) => {
      const y = series.priceToCoordinate(level.price);
      if (y == null || !Number.isFinite(y)) return null;
      let color, lineStyle, lineWidth;
      if (level.role === "entry") {
        color = "#D946EF"; lineStyle = "solid";  lineWidth = 2;
      } else if (level.role === "stop") {
        color = "#EF4444"; lineStyle = "dashed"; lineWidth = 2;
      } else if (level.role === "target") {
        color = "#22C55E"; lineStyle = "dashed"; lineWidth = 2;
      } else {
        return null;
      }
      return (
        <div
          key={level.role}
          style={{
            position:      "absolute",
            left:          leftX + "px",
            top:           y + "px",
            width:         lineWidthPx + "px",
            opacity:       displayOpacity,
            pointerEvents: "none",
            zIndex:        20,
          }}
        >
          <div style={{
            width:     "100%",
            borderTop: `${lineWidth}px ${lineStyle === "dashed" ? "dashed" : "solid"} ${color}`,
          }} />
          <div style={{
            position:     "absolute",
            right:        0,
            top:          "-11px",
            background:   "rgba(0,0,0,0.85)",
            color:        color,
            padding:      "2px 6px",
            borderRadius: "4px",
            fontSize:     "11px",
            fontFamily:   "JetBrains Mono, monospace",
            whiteSpace:   "nowrap",
          }}>
            {level.label}
          </div>
          <div style={{
            position:     "absolute",
            left:         0,
            top:          "-11px",
            background:   "rgba(0,0,0,0.85)",
            color:        color,
            padding:      "2px 6px",
            borderRadius: "4px",
            fontSize:     "10px",
            fontFamily:   "JetBrains Mono, monospace",
            whiteSpace:   "nowrap",
            opacity:      0.7,
          }}>
            {Number(level.price).toFixed(2)}
          </div>
        </div>
      );
    });
  };

const closestCandleIndexByTime = (candlesArr, targetMs) => {
  if (!candlesArr || candlesArr.length === 0 || targetMs == null) return -1;

  let bestIdx = -1;
  let bestDiff = Infinity;

  for (let i = 0; i < candlesArr.length; i++) {
    const cMs = candlesArr[i].timeMs ?? toMs(candlesArr[i].time);
    if (cMs == null) continue;

    const diff = Math.abs(cMs - targetMs);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }

  return bestIdx;
};

    // Render redraw annotation (FROM swing -> TO trigger candle)
    const renderRedrawAnnotation = () => {
    if (!fibonacci || !showRedrawAnnotation) return null;
    if (!fibonacci.redraw_from_price || !fibonacci.redraw_to_price) return null;
    if (!candles || candles.length === 0 || chartHeight < 100) return null;
    
    const priceRange = getPriceRange();
    const fromY = priceToY(fibonacci.redraw_from_price, priceRange, chartHeight);
    const toY = priceToY(fibonacci.redraw_to_price, priceRange, chartHeight);
    
    // Find x positions based on CLOSEST candle time (prevents drift/jumps)
const fromMs = toMs(fibonacci.redraw_from_time);
const toMsVal = toMs(fibonacci.redraw_to_time);

const chartApi = chartRef.current;
if (!chartApi) return null;

const fromX = chartApi.timeScale().timeToCoordinate(normalizeChartTime(fibonacci.redraw_from_time));
const toX = chartApi.timeScale().timeToCoordinate(normalizeChartTime(fibonacci.redraw_to_time));

if (fromX == null || toX == null) return null;
    
    return (
      <>
        {/* FROM point marker (100% - Swing) */}
        <div style={{ 
          position: "absolute", 
          top: fromY + "px", 
          left: fromX + "px", 
          transform: "translate(-50%, -50%)",
          zIndex: 25
        }}>
          <div style={{ 
            width: 12, height: 12, borderRadius: "50%", 
            backgroundColor: "#3B82F6", border: "2px solid #1D4ED8"
          }} />
          <div style={{
            position: "absolute", top: -20, left: "50%", transform: "translateX(-50%)",
            backgroundColor: "rgba(59, 130, 246, 0.9)", color: "#fff",
            padding: "2px 6px", borderRadius: 3, fontSize: 10, fontFamily: "JetBrains Mono, monospace",
            whiteSpace: "nowrap"
          }}>FROM (100%)</div>
        </div>
        
        {/* TO point marker (0% - Trigger) */}
        <div style={{ 
          position: "absolute", 
          top: toY + "px", 
          left: toX + "px", 
          transform: "translate(-50%, -50%)",
          zIndex: 25
        }}>
          <div style={{ 
            width: 12, height: 12, borderRadius: "50%", 
            backgroundColor: "#8B5CF6", border: "2px solid #6D28D9"
          }} />
          <div style={{
            position: "absolute", top: -20, left: "50%", transform: "translateX(-50%)",
            backgroundColor: "rgba(139, 92, 246, 0.9)", color: "#fff",
            padding: "2px 6px", borderRadius: 3, fontSize: 10, fontFamily: "JetBrains Mono, monospace",
            whiteSpace: "nowrap"
          }}>TO (0%)</div>
        </div>
        
        {/* Connecting line */}
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 24 }}>
          <line 
            x1={fromX} y1={fromY} 
            x2={toX} y2={toY} 
            stroke="#8B5CF6" strokeWidth="2" strokeDasharray="8 4" 
          />
        </svg>
      </>
    );
  };

  const renderTrendline = () => {
    if (!fibonacci || !candles || candles.length === 0 || chartHeight < 100) return null;

    const chartApi = chartRef.current;
    const series   = candleSeriesRef.current;
    if (!chartApi || !series) return null;

    const { fromIdx, toIdx } = getSwingIndices();
    if (fromIdx === -1 || toIdx === -1) return null;

    const fromCandle = candles[fromIdx];
    const toCandle = candles[toIdx];

    const normalizeTime = (v) => {
      if (v == null) return null;
      if (typeof v === "number") return v > 1e12 ? Math.floor(v / 1000) : Math.floor(v);
      const ms = Date.parse(v);
      return isNaN(ms) ? null : Math.floor(ms / 1000);
    };

    const fromTime = normalizeTime(fromCandle?.timeMs ?? fromCandle?.timestamp ?? fromCandle?.time);
    const toTime = normalizeTime(toCandle?.timeMs ?? toCandle?.timestamp ?? toCandle?.time);
    if (fromTime == null || toTime == null) return null;

    const xFrom = chartApi.timeScale().timeToCoordinate(fromTime);
    const xTo = chartApi.timeScale().timeToCoordinate(toTime);
    if (xFrom == null || xTo == null) return null;

    const fromPrice = fibonacci.swing_from_price;
    const toPrice = fibonacci.swing_to_price;
    const yFrom = series.priceToCoordinate(fromPrice);
    const yTo = series.priceToCoordinate(toPrice);
    if (yFrom == null || yTo == null) return null;

    return (
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 12 }}>
        {/* Diagonal connector line from swing high wick to swing low wick */}
        <line
          x1={xFrom} y1={yFrom}
          x2={xTo}  y2={yTo}
          stroke="rgba(255,255,255,0.25)"
          strokeWidth="1.5"
          strokeDasharray="6 4"
        />
        {/* Swing HIGH dot — blue, on the high wick */}
        <circle cx={xFrom} cy={yFrom} r="5" fill="#38bdf8" />
        {/* Swing LOW dot — green, on the low wick */}
        <circle cx={xTo} cy={yTo} r="5" fill="#34d399" />
      </svg>
    );
  };


  const renderSwingPoints = () => {
    if (!swingPoints || swingPoints.length === 0 || !candles || candles.length === 0) return null;
    const priceRange = getPriceRange();
    return swingPoints.map((sp, idx) => {
      const y = priceToY(sp.price, priceRange, chartHeight);
      const chartApi = chartRef.current;
      if (!chartApi) return null;
      const x = chartApi.timeScale().timeToCoordinate(normalizeChartTime(sp.time));
      if (x == null) return null;
      const color = sp.type === "high" ? "#22C55E" : "#EF4444";
      return (
        <div key={"swing-" + idx} style={{ position: "absolute", top: y + "px", left: x + "px", transform: "translate(-50%, -50%)" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color, opacity: sp.confirmed ? 1 : 0.5 }} />
        </div>
      );
    });
  };

  const renderMarkers = () => {
    // Native lightweight-charts markers are rendered via candleSeries.setMarkers().
    // Floating HTML overlay markers are intentionally disabled.
    return null;
  };


  return (
    <div style={{ width: "100%", height: "100%", position: "relative", background: "#000" }} data-testid="fibonacci-chart">
      {loading && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 30 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <div style={{ width: 32, height: 32, border: "2px solid #D946EF", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
            <span style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 11, color: "#71717A", textTransform: "uppercase", letterSpacing: "0.05em" }}>Loading chart...</span>
          </div>
        </div>
      )}
      <div ref={chartContainerRef} style={{ width: "100%", height: "100%" }} />
      <div id="ohlc-tooltip" style={{ display:"none", position:"absolute", top:"8px", left:"70px", background:"rgba(0,0,0,0.85)", border:"1px solid #27272A", borderRadius:"4px", padding:"4px 10px", fontSize:"11px", fontFamily:"JetBrains Mono, monospace", pointerEvents:"none", zIndex:50, whiteSpace:"nowrap" }} />
      <div key={chartVersion} style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "visible", zIndex: 10 }}>
          {chartReady && renderHistoricalZones()}
        {(chartReady || chartVersion >= 0) && renderTrendline()}
        {chartReady && renderFibLevels()}
        {chartReady && renderRedrawAnnotation()}
        {chartReady && renderSwingPoints()}
        {chartReady && renderMarkers()}
      </div>
      {fibonacci && (
        <div style={{ position: "absolute", bottom: 12, left: 12, background: "rgba(0,0,0,0.8)", padding: "6px 10px", borderRadius: 4, border: "1px solid #27272A", zIndex: 20 }}>
          <div style={{ fontSize: 10, color: "#71717A", textTransform: "uppercase", letterSpacing: "0.05em" }}>Structure</div>
          <div style={{ fontSize: 11, color: "#A1A1AA", fontFamily: "JetBrains Mono, monospace" }}>
            {fibonacci.status === "invalidated" 
              ? `Redrawn: ${fibonacci.invalidation_reason?.replace(/_/g, ' ') || 'structure break'}`
              : `${fibonacci.is_uptrend ? '↑ Bullish' : '↓ Bearish'} (${fibonacci.timeframe})`}
          </div>
          {fibonacci.redraw_from_price && showRedrawAnnotation && (
            <div style={{ fontSize: 10, color: "#8B5CF6", marginTop: 4 }}>
              FROM ${fibonacci.redraw_from_price?.toFixed(2)} → TO ${fibonacci.redraw_to_price?.toFixed(2)}
            </div>
          )}
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default FibonacciChart;
